import {useLazyQuery} from "@apollo/client";
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import {useFormContext} from "react-hook-form";

import {SelectField, SelectFieldBaseProps} from "@/components/form/fields/SelectField/SelectField.tsx";
import {TagSelect} from "@/components/TagSelect/TagSelect.tsx";
import {useLoadOptions} from "@/lib/hooks/useLoadOptions.ts";
import {GQL_TAGS, TagsResponse} from "@/lib/queries/checks.ts";
import {ServiceTag} from "@/lib/types/checks.ts";

interface TagsFieldProps extends SelectFieldBaseProps {
  tags?: ServiceTag[];
  value?: number[];
  onChange?: (value: number[]) => void;
}

export const TagsField = ({
  tags: options,
  value: propsValue,
  onChange,
  name,
  placeholder = "Select Tags...",
  ...rest
}: TagsFieldProps) => {
  const {
    watch,
    setValue,
    formState: {defaultValues},
  } = useFormContext();
  const value = propsValue ? propsValue : watch(name);
  const defaultValue = get(defaultValues, name);

  const [getTags] = useLazyQuery<TagsResponse>(GQL_TAGS);
  const {options: tags, isLoading} = useLoadOptions<ServiceTag, TagsResponse, false>({
    options: options,
    fetchOptions: getTags,
    transform: (result) => Array.from(result.data?.tags || []),
  });

  return (
    <SelectField
      name={name}
      placeholder={placeholder}
      {...rest}
      select={
        <TagSelect
          isMulti={true}
          isLoading={isLoading}
          options={sortBy(tags, (tag) => tag.tag)}
          defaultValue={tags ? tags.filter((t) => t.id === defaultValue) : null}
          value={tags ? tags.filter((t) => (Array.isArray(value) ? value.includes(t.id) : false)) : []}
          onChange={(v) => {
            if (onChange) {
              onChange(v.map((t) => t.id));
            } else {
              setValue(
                name,
                v.map((t) => t.id),
                {shouldDirty: true, shouldValidate: true}
              );
            }
          }}
        />
      }
    />
  );
};
